/**
 * Datasource remoto User: abstração + implementação Firestore.
 * Recebe Firestore por injeção (clean architecture); se não passado, usa getFirestoreDb().
 */

import type { Firestore } from 'firebase/firestore';
import {
  doc,
  getDoc,
  setDoc,
  deleteDoc,
  collection,
  getDocs,
  onSnapshot,
} from 'firebase/firestore';
import { getFirestoreDb } from '@/core/services';
import { normalizeUserFields, User, UserRef, userSchema } from '@/core/domain';

export interface UserRemoteDataSource {
  getById(id: string): Promise<User | null>;
  getAll(): Promise<User[]>;
  create(user: User): Promise<User>;
  update(id: string, user: Partial<User>): Promise<User>;
  delete(id: string): Promise<void>;
  /**
   * Escuta o documento do usuário em tempo real.
   * @return Função para cancelar a inscrição.
   */
  subscribeById(
    id: string,
    onNext: (user: User | null) => void,
    onError?: (error: Error) => void
  ): () => void;
}

function toUser(id: string, data: Record<string, unknown>): User {
  const normalized = normalizeUserFields(id, data);
  const parsed = userSchema.safeParse(normalized);
  if (!parsed.success) throw new Error('Invalid user from Firestore');
  return parsed.data;
}

export class UserFirestoreDataSource implements UserRemoteDataSource {
  constructor(private readonly db?: Firestore) {}

  private getDb(): Firestore {
    return this.db ?? getFirestoreDb();
  }

  async getById(id: string): Promise<User | null> {
    const ref = doc(this.getDb(), UserRef.collection, id);
    const snap = await getDoc(ref);
    if (!snap.exists()) return null;
    return toUser(snap.id, snap.data() as Record<string, unknown>);
  }

  async getAll(): Promise<User[]> {
    const ref = collection(this.getDb(), UserRef.collection);
    const snap = await getDocs(ref);
    return snap.docs.map((d) => toUser(d.id, d.data() as Record<string, unknown>));
  }

  /**
   * Grava o documento na coleção users usando user.id como id do documento.
   * Não gera novo UID: o id deve ser o UID do Firebase Auth para manter a relação.
   */
  async create(user: User): Promise<User> {
    const id = user?.id;
    if (id == null || typeof id !== 'string' || id.length === 0) {
      throw new Error('User id (Firebase Auth UID) is required');
    }
    const ref = doc(this.getDb(), UserRef.collection, id);
    await setDoc(ref, user);
    return user;
  }

  async update(id: string, user: Partial<User>): Promise<User> {
    const ref = doc(this.getDb(), UserRef.collection, id);
    const snap = await getDoc(ref);
    if (!snap.exists()) throw new Error('User not found');
    const current = toUser(snap.id, snap.data() as Record<string, unknown>);
    const updated = { ...current, ...user, id };
    await setDoc(ref, updated);
    return updated;
  }

  async delete(id: string): Promise<void> {
    const ref = doc(this.getDb(), UserRef.collection, id);
    await deleteDoc(ref);
  }

  subscribeById(
    id: string,
    onNext: (user: User | null) => void,
    onError?: (error: Error) => void
  ): () => void {
    const ref = doc(this.getDb(), UserRef.collection, id);
    return onSnapshot(
      ref,
      (snap) => {
        if (!snap.exists()) {
          onNext(null);
          return;
        }
        onNext(toUser(snap.id, snap.data() as Record<string, unknown>));
      },
      (err) => {
        onError?.(err instanceof Error ? err : new Error(String(err)));
      }
    );
  }
}
